import React, { useContext, useEffect, useState } from "react";
import { Layout, Card, PageActions, Select, FormLayout, List, TextContainer } from "@shopify/polaris";
import { useQuery } from "@apollo/react-hooks";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// import constant
import constant from "lib/constant/constant";

// import context
import { PrivateContext, OnboardingContext } from "lib/context";

// import query
import GET_PRODUCT_SETTING from "app/setup/apollo/queries/getProductSetting";

// import components
import Banner from "lib/components/banner/banner";

import { baseHelper } from "lib/helpers";
import { SkeletonAnnotated } from "lib/components";
import ProductTabs from "./subFeatures/form/subFeatureItem/tab";
import Connect from "./subFeatures/connect/connect";
import BigCommerceConnect from "./subFeatures/bigCommerceConnect/bigCommerceConnect";
import addData from "./config/addData";

const { CREATE_FORM } = constant;

const ProductAdd = () => {
  const { cms, history, isLoading, currentUser } = useContext(PrivateContext);
  const { ecommercePlatform } = currentUser;
  const { gql } = constant;
  const { isOnboarding, onPrevious } = useContext(OnboardingContext);
  const { search, state } = history.location;
  const { shop: isShopConnected, key = "" } = baseHelper.getQueryParams(search);
  const [isSelectChange, setIsSelectChange] = useState(false);
  const [count, setCount] = useState(0);
  const [banner, setBanner] = useState({
    action: null,
    children: null,
    isOpen: false,
    status: "",
    title: "",
  });
  const [selected, setSelected] = useState("");
  const { data: productSettingsData, refetch } = useQuery(GET_PRODUCT_SETTING);

  useEffect(() => {
    setCount(count + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state && state.bannerData) {
      setBanner({
        isOpen: true,
        status: state.bannerData.status,
        title: state.bannerData.title,
      });
      delete state.bannerData;
      history.replace({ ...history.location, state });
    }
  }, [history, state]);

  useEffect(() => {
    const responseError = baseHelper.getResponseError(productSettingsData, gql.GET_PRODUCT_SETTING);
    if (responseError) {
      setBanner({ isOpen: true, title: responseError, status: constant.CRITICAL });
    }
  }, [gql.GET_PRODUCT_SETTING, productSettingsData, refetch]);

  const renderComponent = (value) => {
    if (value === CREATE_FORM) {
      return <ProductTabs setBanner={setBanner} />;
    }
    if (value === constant.SHOPIFY || (isShopConnected && count === 1)) {
      return <Connect setBanner={setBanner} />;
    }
    if (value === constant.BIGCOMMERCE || (isShopConnected && count === 1)) {
      return <BigCommerceConnect setBanner={setBanner} path="bigcommerce" />;
    }
    return null;
  };
  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const standardLists = cms("section.onboarding.caption") || [];
  const renderCaptions = () => {
    const list = standardLists.map((content) => (
      <List.Item>
        <b>{content.title}</b>
        {content.description}
      </List.Item>
    ));
    return list;
  };

  const getOptions = (options) => {
    let integration = [constant.SHOPIFY, constant.WOOCOMMERCE, constant.WIX];

    if (isOnboarding) {
      return options.filter((option) => !integration.includes(option.value));
    }

    integration = process.env.REACT_APP_ENV === "prod" ? integration.slice(2) : [];

    return options.filter(
      (option) =>
        !integration.includes(option.value) &&
        !(ecommercePlatform !== constant.BIGCOMMERCE && option.value === constant.SHOPIFY)
    );
  };

  const handleSelectChange = (updatedValue) => {
    setIsSelectChange(true);
    setSelected(updatedValue);
  };

  if (isLoading) {
    return <SkeletonAnnotated />;
  }

  const wixSelected = key === constant.WIX ? constant.WIX : selected;

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            action={banner.action}
            onDismiss={onDismissBanner}
          >
            {banner.children}
          </Banner>
        </Layout.Section>
      )}
      <br />
      <Layout.AnnotatedSection
        title={cms("label.import")}
        description={cms("label.csvInfo", { store: ecommercePlatform })}
      >
        <Card>
          <Card.Section>
            {isOnboarding && (
              <>
                <TextContainer>
                  <p>{cms("section.onboarding.subDescription")}</p>
                  <p>{cms("section.onboarding.note")}</p>
                </TextContainer>

                <br />
                <TextContainer>
                  <Banner status={constant.CRITICAL} isOpen>
                    <p>{cms("section.onboarding.message.error.manual")}</p>
                  </Banner>
                </TextContainer>
                <br />
              </>
            )}
            {/* {!isOnboarding && (
              <>
                <TextContainer>
                  <p>{cms("section.onboarding.manual")}</p>

                  <p>{cms("section.onboarding.note")}</p>
                </TextContainer>
                <br />
              </>
            )} */}
            <FormLayout>
              <List type="bullet">
                <List.Item>{cms("label.manual")}</List.Item>
                {/* <List.Item>{cms("label.upload")}</List.Item> */}
              </List>
              <Select
                id="addProduct"
                options={getOptions(addData(cms, isOnboarding).options)}
                onChange={handleSelectChange}
                placeholder={cms("label.choose")}
                value={isShopConnected && count === 1 ? constant.SHOPIFY : wixSelected}
              />
              {isOnboarding && (
                <Banner status="info" isOpen>
                  <p>{cms("section.onboarding.options")}</p>
                  <br />
                  <p>
                    <List type="bullet">
                      <List type="bullet">{renderCaptions()}</List>
                    </List>
                  </p>
                </Banner>
              )}
            </FormLayout>
          </Card.Section>
        </Card>
        {renderComponent(selected)}

        {!(isSelectChange || isShopConnected) && isOnboarding && (
          <PageActions
            secondaryActions={[
              {
                content: cms("common.button.previous"),
                onAction: onPrevious,
              },
            ]}
          />
        )}
      </Layout.AnnotatedSection>
    </>
  );
};

export default withFeature(withErrorBoundary(ProductAdd), { feature: constant.PRODUCT_ADD });
